import JSZip from "jszip";
import pdfMake from 'pdfmake';
import { createNote, realizeAggregateJobNotes, realizeJobNotes } from "../reducers/jobNoteSlice";
import { JobPrinterFactory } from "./jobPrinting";
import { realizeRelatedJobs } from "../reducers/jobRelationSlice";
import { getJobRelatedDocData, realizeJobRelatedDocs } from "../reducers/jobRelatedDocSlice";
import { realizeJobForms } from "../reducers/jobCustomFormSlice";
import { realizeWeather, realizeWeatherInRange } from "../reducers/weatherSlice";
import { getJobImageData, realizejobImages } from "../reducers/jobImageSlice";
import { generateDocx } from "../common/utils/docxUtilities";
import { cdate, now } from "../common/utils/CDate";
import { realizeJobAssistance } from "../reducers/jobAssistanceSlice";
import { nowISOString, parseDate } from "../common/utils/dateUtils";
import { realizeGlossary } from "../reducers/glossarySlice";
import { realizeSite } from "../reducers/siteSlice";
import { realizeLevel } from "../reducers/levelSlice";
import { realizeType } from "../reducers/typeSlice";
import { realizeProblem } from "../reducers/problemSlice";
import { realizeJobTags } from "../reducers/jobTagSlice";
import { realizePriorities } from "../reducers/prioritySlice";
import { withRealizationScope } from "../common/hooks/initHook";
import { deepClone } from "../common/utils/objectUtilities";
import { realizeJobs } from "../reducers/jobSlice";
import { dynamicSort } from "../common/utils/tableUtilities";

export const printJobs = async (setProgress, realizationContext, lang, jobs, note, username, start, end, options = {}) => {
    const {clientConfig: configuration, t, network } = realizationContext;
    const { batchSize = 10, maxBatches = 50, templateDoc, context, onError } = options;
    return await withRealizationScope(realizationContext, async realizationContext => {
        setProgress(0);
        let processedBatches = 0;
        let totalBatches = Math.ceil(jobs.length / batchSize);
        let zip = new JSZip();
        const queue = deepClone(jobs);

        const downloadZip = async (id) => {
            const content = await zip.generateAsync({ type: 'blob' });
            const filename = t('job', {count: 100}) + id + '.zip';
            network.downloadBlob(content, filename);
            zip = new JSZip();
        }

        const archiveJob = (buffer, job, images, docs) => {
            let utf8 = new Uint8Array(buffer);
            let filename = job.displayid + '.pdf';
            zip.file(filename, utf8);
            images.forEach(img => {
                if (img?.data) {
                    // lop off the prefix (e.g. 'data:application/octet-stream;base64,')
                    let idx = img.data.indexOf(',') + 1;
                    zip.file(job.displayid + '_images/' + img.filename + '.jpg', img.data.substr(idx), { base64: true });
                }
            })
            docs.forEach(doc => {
                if (doc?.data) {
                    zip.file(job.displayid + '_docs/' + doc.filename, doc.data);
                }
            });
        }
        try {
            if (!templateDoc) {
                while (queue.length > 0) {
                    const currentBatch = queue.splice(0, batchSize);
                    const glossary = await realizeGlossary(realizationContext);

                    await Promise.allSettled(currentBatch.map(async j => {
                        const site = await realizeSite(realizationContext, j.site);
                        const level = await realizeLevel(realizationContext, j.site, j.level);
                        const type = await realizeType(realizationContext, j.site, j.level, j.type);
                        const problem = await realizeProblem(realizationContext, j.site, j.level, j.type, j.problem);
                        const tags = await realizeJobTags(realizationContext);
                        const priorities = await realizePriorities(realizationContext);
                                            
                        let [related, docInfo, forms, imageInfo, notes, weather] = (await Promise.allSettled([realizeRelatedJobs(realizationContext, j.jobid),
                                                                                                realizeJobRelatedDocs(realizationContext, j.jobid),
                                                                                                realizeJobForms(realizationContext, j.jobid),
                                                                                                realizejobImages(realizationContext, j.jobid),
                                                                                                realizeJobNotes(realizationContext, j.jobid),
                                                                                                configuration.enableWeather ? realizeWeather(realizationContext, j.created) : Promise.resolve({data: null})])).map(r => r.value);
                                                                                                
                        related = related ?? [];
                        docInfo = docInfo ?? [];
                        forms = forms ?? [];
                        imageInfo = imageInfo ?? [];
                        notes = [...notes] ?? [];
                        notes.sort(dynamicSort(['timestamp']));
                        const imgData = await Promise.allSettled(imageInfo.map(img => getJobImageData(network, j.jobid, img.filename)));
                        let images = imageInfo.map((img, idx) => ({...img, data: imgData[idx]?.value}));
                        
                        let docsToDownload = docInfo?.filter(d => d.file) ?? []
                        const docData = await Promise.allSettled(docsToDownload.map(d => getJobRelatedDocData(network, d.jdocid)));
                        let docs = docsToDownload.map((d, idx) => ({...d, data: docData[idx]?.value}))
                        
                        const jobPrinter = JobPrinterFactory(realizationContext, lang);
                        createNote(network, { author: username, username:username, jobid: j.jobid, note });
                        
                        let pdf = await jobPrinter.makeLog(j, site, level, type, problem, priorities, tags, await realizeJobs(realizationContext, related.map(r => r.relatedid)), related, docs, forms, images, notes, weather, glossary, username)
                        let temp = pdfMake.createPdf(pdf, null, { 
                            Roboto: {
                                normal: 'dejavusans.ttf',
                                bold: 'dejavusans-bold.ttf',
                                italics: 'dejavusans-oblique.ttf',
                                bolditalics: 'dejavusans-oblique.ttf'
                            }
                        })
                        return new Promise((f, r) => {
                            temp.getBuffer(buffer => {archiveJob(buffer, j, images, docs); f()});
                        })
                    }));
                    processedBatches++;
                    setProgress(Math.floor(processedBatches / totalBatches * 100));
                    if (processedBatches % maxBatches === 0) {
                        await downloadZip(processedBatches / maxBatches);
                    }
                }
                if (processedBatches % maxBatches !== 0) {
                    await downloadZip(Math.ceil(processedBatches / maxBatches));
                }
            } else {
                const includeNotes = !!configuration?.reportWithNotes;
                const totalSteps = Math.max(1, jobs?.length + 4); 
                let completedSteps = 0;
                const bumpProgress = () => {
                    completedSteps += 1;
                    setProgress(Math.floor(completedSteps / totalSteps * 100));
                };

                const doc = await network.fetchData('/reportDocx/' + encodeURIComponent(templateDoc.id));
                bumpProgress();
                const priorities = await realizePriorities(realizationContext);
                bumpProgress();
                const alljobs = await Promise.all(jobs.map(async (job) => {
                    const assistance = (await realizeJobAssistance(realizationContext, job.jobid)) ?? [];
                    bumpProgress();
                    return { ...job, assistance };
                }));
                const notes = Object.entries(includeNotes ? (await realizeAggregateJobNotes(realizationContext, jobs.map(j => j.jobid))) : {}).reduce((notes, [jobid, jobNotes]) => {
                    jobNotes = [...jobNotes];
                    jobNotes.sort(dynamicSort(['timestamp']));
                    notes[jobid] = jobNotes;
                    return notes;
                }, {});
                
                bumpProgress();
                const weather = await realizeWeatherInRange(realizationContext, start, end ?? nowISOString());
                bumpProgress();
                const result = await generateDocx(configuration, network, {
                    template: await doc.arrayBuffer(),
                    data: {
                        eventName: configuration.eventName,
                        enableWeather: configuration.enableWeather,
                        weather,
                        start: null, 
                        interval: null,
                        jobTerm: t('job_one'),
                        summary: { alljobs, jobs: alljobs, notes, context },
                        charts: configuration.reportCharts,
                    },
                    noSandbox: true,
                    additionalJsContext: {
                        priorityName: function (v) {
                            return priorities.find(p => p.internalid == v)?.text
                        },
                        computeDurations: function (type) {
                            let max = 12;
                            let jobsToProcess = alljobs;
                            if (type != null) {
                                jobsToProcess = jobsToProcess.filter(function (j) { return j.type == type || (j.assistance?.some(a => a.type == type)); });
                            }
                            let toReturn = {};
                            for (let i = 0; i < max; ++i) {
                                let hrs = Math.floor(i / 4);
                                let mins = ((i % 4) * 15);
                                toReturn[i] = { data: 0, label: (hrs + ':' + (mins == 0 ? '00' : mins)) };
                            }
                            toReturn[max - 1].label += '+';
                            for (const job of jobsToProcess) {
                                let durationMins = cdate(job.closed ?? job.lastupdate).diff(cdate(job.created), 'minutes');
                                let idx = Math.floor(durationMins / 15);
                                if (idx >= max) {
                                    idx = max - 1;
                                } else if (idx < 0) {
                                    continue;
                                }
                                toReturn[idx].data += 1;
                            }
                            return toReturn;
                        },
                        computeIntervals: function (type, field) {
                            const tz = configuration.tz;
                            const jobs = type != null ? alljobs.filter(j => j.type === type || j.assistance?.some(a => a.type === type)) : alljobs;
                            const intervals = jobs.reduce((map, job) => {
                                const raw = job?.[field];
                                if (!raw) {
                                    return map;
                                }
                                const mo = cdate(raw, tz);
                                if (!mo.isValid()) {
                                    return map;
                                }
                                const dayStart = mo.startOf('day');
                                const lowerBound = dayStart.clone().hours(6);
                                const serviceDay = mo.isBefore(lowerBound) ? dayStart.subtract(1, 'days') : dayStart;
                                const key = serviceDay.customFormat('Do MMMM YYYY', false);
                                const existing = map.get(key);
                                const data0 = existing?.data ?? Array(24).fill(0);
                                const labels0 = existing?.labels ?? Array.from({ length: 24 }, (_, j) => (6 + j) % 24);

                                const idx = ((mo.hours() - 6) + 24) % 24;
                                const nextData = [...data0];
                                nextData[idx] = (nextData[idx] ?? 0) + 1;
                                    
                                map.set(key, { data: nextData, labels: labels0 });
                                return map;
                            }, new Map());
                            const max = Math.max(0, ...[...intervals.values()].flatMap(v => v.data));
                            return Object.fromEntries([...intervals.entries()].map(([k, v]) => [k, { ...v, max }]));                           
                        },
                        computeWeatherDatasets: function (weather) {
                            if (!Array.isArray(weather) || weather.length < 1) {
                                return { data: { temp: [0], wind: [0], humidity: [0] }, labels: ["0"] };
                            }
                            
                            const start = parseDate(weather[0].timestamp);
                            const end   = parseDate(weather[weather.length - 1].timestamp);
                            const count = 36;

                            const buckets = weather.reduce((m, w) => {
                                const ts = w?.timestamp; 
                                if (!ts) { 
                                    return m;
                                }

                                const d = parseDate(ts);
                                if (isNaN(d)) { 
                                    return m;
                                }
                                    
                                const idx = Math.floor(((d - start) / (end - start)) * count)
                                if (idx < 0 || idx >= count) {
                                    return m;
                                }
                                    
                                const b = (m[idx] ??= {});
                                b.label = b.label ?? d;
                                b.t = w.temperature;
                                b.w = w.windspeed;
                                b.h = w.humidity;
                                return m;
                            }, {});
                        
                            const data = { temp: [], wind: [], humidity: [] };
                            let pt = 0, pw = 0, ph = 0;
                            for (let i = 0; i < count; i++) {
                                const b = buckets[i];
                                if (b && b.t !== undefined) {
                                    pt = b.t;
                                }
                                if (b && b.w !== undefined) {
                                    pw = b.w;
                                }
                                if (b && b.h !== undefined) {
                                    ph = b.h;
                                }
                                    
                                data.temp.push(pt);
                                data.wind.push(pw);
                                data.humidity.push(ph);
                            }

                            const labels = Array.from({ length: count }, (_, i) => {
                                const b = buckets[i];
                                return b && b.label ? cdate(b.label, configuration.tz).dateTimeFormat(): "";
                            });
                            return { data, labels };
                        }
                    }
                });
                bumpProgress();
                const blob = new Blob([result.data], { type: result.mime });
                network.downloadBlob(blob, templateDoc.name + '_' + now(configuration.tz).dateTimeFormat() + '.' + result.type);
            }
        } catch (error) {
            onError?.(error);
        } finally {
            setProgress();
        }
    })
}